import React from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { userPrivileges } from '../../../apollo/cache';
import { useReactiveVar } from '@apollo/client';
import CustomizationPage from '../../appraisalView/components/CustomizationPage';
import WelcomePage from '../../welcomePage/components/WelcomePage';
import IndustryReportsNavigationPage from '../../industryReports/components/IndustryReportsNavigationPage';
import BaselineReportsPage from '../../industryReports/components/BaselineReportsPage';
import AppraisalViewPage from '../../appraisalView/components/AppraisalViewPage';
import PrivilegeNames from '../../../constants/privilegeNames';
import AppraisalComparison from '../../appraisalComparison/components/AppraisalComparison.js';
import UnitSearchPage from '../../unitSearch/components/UnitSearchPage';
import ReportManagementPage from '../../reportManagement/components/ReportManagementPage';
import CheckPointsListingPage from '../../checkPoints/components/CheckPointsListingPage';
import ViewCheckPointPage from '../../checkPoints/components/viewCheckPoint/ViewCheckPointPage';
import LicenseManagementPage from '../../licenseManagement/components/LicenseManagementPage';
import NotFoundPage from '../../notFound/NotfoundPage';
import UserManagementPage from '../../userManagement/components/UserManagementPage';
import AssignLicensePage from '../../userManagement/components/assignLicense/AssignLicensePage';
import ReleaseNotesPage from '../../releaseNotes/components/ReleaseNotesPage';
import EditLicensePage from '../../userManagement/components/editLicense/EditLicensePage';
import ResourcePage from '../../resources/components/ResourcesPage';
import NoPermissionsPage from '../../unauthorized/NoPermissionsPage';
import AddPerformanceMetric from '../../checkPoints/components/performanceMetric/addPerformanceMetric/addPerformanceMetric';
import EditPerformanceMetric from '../../checkPoints/components/performanceMetric/editPerformanceMetric/editPerformanceMetric';
import ViewPerformanceMetric from '../../checkPoints/components/performanceMetric/viewPerformanceMetric/viewPerformanceMetric';
import SubmitFeedbackPage from '../../feedback/components/SubmitFeedbackPage';
import AccountPage from '../../account/components/AccountPage';
import UserManagementRedirect from './UserManagementRedirect';
import CalendarPage from '../../calendar/components/CalendarPage';
import { DataDownloadPage } from '../../dataDownload/components/DataDownloadPage';
import AppraiserProfilePage from '../../userManagement/components/editLicense/AppraiserProfilePage/AppraiserProfilePage.js';
import AddEventPage from '../../calendar/components/CalendarEvents/AddEventPage/AddEventPage.js';
import EditEventPage from '../../calendar/components/CalendarEvents/EditEventPage/EditEventPage';
import CheckpointPage from '../../checkPoints/components/checkPoint/CheckpointPage';
import { PAGE_VIEW_MODES } from '../../../constants/page-view-modes';
import ReappraisalRationaleReviewPage from '../../reappraisalRationaleReview/components/ReappraisalRationaleReviewPage';
import EventDateChangeReviewPage from '../../eventDateChangeReviewPage/components/EventDateChangeReviewPage';
import RouteParamReloader from './RouteParamReloader';
import RedirectPage from '../../redirectPage/components/RedirectPage';
import StrengthsAndChallengesPage from '../../strengthsAndChallenges/components/StrengthsAndChallengesPage';
import InvoicesPage from '../../invoices/components/Invoices';
import LegalDocumentsWrapper from '../../legalDocuments/components/LegalDocumentsWrapper';
import ExecutiveSummaryOrganizationSearch from '../../organizationSearch/components/ExecutiveSummaryOrganizationSearch';
import UserManagementOrganizationSearch from '../../organizationSearch/components/UserManagementOrganizationSearch';
import OrganizationReportsNavigationPage from '../../organizationReports/components/OrganizationReportsNavigationPage';
import OrganizationReportsPage from '../../organizationReports/components/OrganizationReportsPage';
import ExecutiveSummaryWrapper from '../../executiveSummary/components/ExecutiveSummaryWrapper';
import LegalDocumentsOrganizationSearch from '../../organizationSearch/components/LegalDocumentsOrganizationSearch';
import AggregatePerformanceReport from '../../aggregatePerformanceReport/components/AggregatePerformanceReport';
import DataCorrelationsPage from '../../dataCorrelations/components/DataCorrelationsPage';
import SurveyResultsPage from '../../survey/components/SurveyResultsPage';
import { ObservationReportSubmissionPage } from '../../observationReport/components/ObservationReportSubmissionPage';
import { ObservationReportReviewPage } from '../../observationReport/components/ObservationReportReviewPage';
import CertificationPathwayProgressPage from '../../userManagement/components/certificationPathwayProgress/CertificationPathwayProgressPage';

const RoutesForRegularRoles = () => {
  const privileges = useReactiveVar(userPrivileges);
  if (!privileges?.length) {
    return null;
  }

  return (
    <Routes>
      <Route path='/data-reports'>
        {privileges.includes(PrivilegeNames.WelcomePage) && (
          <Route path='' element={<WelcomePage />} />
        )}
        <Route path='industry-reports'>
          {privileges.includes(PrivilegeNames.IndustryReports) && (
            <Route path='' element={<IndustryReportsNavigationPage />} />
          )}
          {privileges.includes(PrivilegeNames.BaseLine) && (
            <Route path='baseline' element={<BaselineReportsPage />} />
          )}
          {privileges.includes(PrivilegeNames.Customization) && (
            <Route path='customization' element={<CustomizationPage />} />
          )}
          {privileges.includes(PrivilegeNames.AppraisalView) && (
            <Route path='customization/appraisal-view' element={<AppraisalViewPage />} />
          )}
          {privileges.includes(PrivilegeNames.AppraisalComparison) && (
            <Route path='appraisal-comparison'>
              <Route path=':orgUnit' element={<AppraisalComparison />} />
              {privileges.includes(PrivilegeNames.UnitSearch) && (
                <Route path='unit-search' element={<UnitSearchPage />} />
              )}
              {privileges.includes(PrivilegeNames.UnitSelection) && (
                <Route path='unit-selection' element={<UnitSearchPage />} />
              )}
            </Route>
          )}
          {privileges.includes(PrivilegeNames.StrengthsAndChallenges) && (
            <Route path='strengths-challenges' element={<StrengthsAndChallengesPage />} />
          )}
          {privileges.includes(PrivilegeNames.AggregatePerformanceReport) && (
            <Route path='aggregate-performance-report' element={<AggregatePerformanceReport />} />
          )}
          {privileges.includes(PrivilegeNames.DataCorrelations) && (
            <Route path='data-correlations' element={<DataCorrelationsPage />} />
          )}
        </Route>

        {privileges.includes(PrivilegeNames.OrganizationReports) && (
          <Route path='organization-reports'>
            <Route path='' element={<OrganizationReportsNavigationPage />} />
            {privileges.includes(PrivilegeNames.OrganizationReports_UnitReports) && (
              <Route path='unit-reports'>
                <Route path=':orgUnit' element={<OrganizationReportsPage />} />
                {privileges.includes(PrivilegeNames.UnitSelection) && (
                  <Route path='unit-selection' element={<UnitSearchPage />} />
                )}
                {privileges.includes(PrivilegeNames.UnitSearch) && (
                  <Route path='unit-search' element={<UnitSearchPage />} />
                )}
              </Route>
            )}

            {privileges.includes(PrivilegeNames.OrganizationReports_ExecutiveSummary) && (
              <Route path='executive-summary'>
                <Route path=':orgId?' element={<ExecutiveSummaryWrapper />} />
                {privileges.includes(PrivilegeNames.OrganizationSearch_Standard) && (
                  <Route
                    path='organization-search'
                    element={<ExecutiveSummaryOrganizationSearch />}
                  />
                )}
              </Route>
            )}
          </Route>
        )}
      </Route>
      {privileges.includes(PrivilegeNames.CheckPoints) && (
        <Route path='/check-points'>
          {privileges.includes(PrivilegeNames.UnitSearch) && (
            <Route path='unit-search' element={<UnitSearchPage />} />
          )}
          {privileges.includes(PrivilegeNames.UnitSelection) && (
            <Route path='unit-selection' element={<UnitSearchPage />} />
          )}
          <Route path=':orgUnit'>
            <Route path='' element={<CheckPointsListingPage />} />
            <Route path='add-check-point/:appraisal/:checkPointId'>
              <Route path='' element={<CheckpointPage pageMode={PAGE_VIEW_MODES.ADD} />} />
              <Route
                path='add-metric'
                element={<AddPerformanceMetric pageMode={PAGE_VIEW_MODES.ADD} />}
              />
              <Route
                path='edit-metric'
                element={<EditPerformanceMetric pageMode={PAGE_VIEW_MODES.ADD} />}
              />
            </Route>
            <Route path='edit-check-point/:appraisal/:checkPointId'>
              <Route path='' element={<CheckpointPage pageMode={PAGE_VIEW_MODES.EDIT} />} />
              <Route
                path='add-metric'
                element={<AddPerformanceMetric pageMode={PAGE_VIEW_MODES.EDIT} />}
              />
              <Route
                path='edit-metric'
                element={<EditPerformanceMetric pageMode={PAGE_VIEW_MODES.EDIT} />}
              />
            </Route>
            <Route path='view-check-point/:appraisal/:checkPoint'>
              <Route path='' element={<ViewCheckPointPage />} />
              <Route path='view-metric/:metric' element={<ViewPerformanceMetric />} />
            </Route>
          </Route>
        </Route>
      )}

      {privileges.includes(PrivilegeNames.ReportManagement) && (
        <Route path='/report-management'>
          <Route path=':orgUnit' element={<ReportManagementPage />} />
          {privileges.includes(PrivilegeNames.UnitSearch) && (
            <Route path='unit-search' element={<UnitSearchPage />} />
          )}
          {privileges.includes(PrivilegeNames.UnitSelection) && (
            <Route path='unit-selection' element={<UnitSearchPage />} />
          )}
        </Route>
      )}
      {privileges.includes(PrivilegeNames.UserManagement) && (
        <>
          <Route path='/user-management'>
            <Route path=':org?' element={<UserManagementPage />} />
            <Route path='assign-license' element={<AssignLicensePage />} />
            <Route path='edit-license' element={<EditLicensePage />} />
            <Route path=':orgId/appraiser-profile/:userId' element={<AppraiserProfilePage />} />
            {privileges.includes(PrivilegeNames.UserManagement_CertificationPathwayProgress) && (
              <Route
                path='certification-pathway-progress'
                element={<CertificationPathwayProgressPage />}
              />
            )}
            {privileges.includes(PrivilegeNames.OrganizationSearch_Advanced) && (
              <Route path='organization-search' element={<UserManagementOrganizationSearch />} />
            )}
            {privileges.includes(PrivilegeNames.LicenseManagement) && (
              <Route path='license-management' element={<LicenseManagementPage />} />
            )}
          </Route>
          <Route
            path='/user-management-redirect/:orgId/:isFree'
            element={<UserManagementRedirect />}
          />
        </>
      )}
      {privileges.includes(PrivilegeNames.Resources) && (
        <Route path='/resources' element={<ResourcePage />} />
      )}
      {privileges.includes(PrivilegeNames.ReleaseNotes) && (
        <Route path='/release-notes' element={<ReleaseNotesPage />} />
      )}
      {privileges.includes(PrivilegeNames.PostAppraisalSurvey) && (
        <Route path='/survey' element={<SurveyResultsPage />} />
      )}
      {privileges.includes(PrivilegeNames.SubmitFeedback) && (
        <Route path='/submit-feedback' element={<SubmitFeedbackPage />} />
      )}
      {(privileges.includes(PrivilegeNames.EmailPreferences) ||
        privileges.includes(PrivilegeNames.AppraiserProfile)) && (
        <Route path='/account' element={<AccountPage />} />
      )}
      {privileges.includes(PrivilegeNames.DataDownloadPage) && (
        <Route path='/data-download' element={<DataDownloadPage />} />
      )}
      {privileges.includes(PrivilegeNames.Calendar) && (
        <Route path='/calendar'>
          <Route path='' element={<CalendarPage />} />
          <Route
            path='edit-event/:eventId'
            element={<RouteParamReloader component={EditEventPage} />}
          />
          {privileges.includes(PrivilegeNames.Calendar_AddEvent) && (
            <Route path='add-event' element={<AddEventPage />} />
          )}
        </Route>
      )}
      {privileges.includes(PrivilegeNames.ReappraisalRationaleReview) && (
        <Route path='/reappraisal-rationale'>
          <Route path=':checkPointId' element={<ReappraisalRationaleReviewPage />} />
        </Route>
      )}

      {privileges.includes(PrivilegeNames.Calendar_ChangeDateRequestReview) && (
        <Route path='/date-change-review/:requestId' element={<EventDateChangeReviewPage />} />
      )}

      {privileges.includes(PrivilegeNames.Invoices) && (
        <Route path='/invoices' element={<InvoicesPage />} />
      )}
      {privileges.includes(PrivilegeNames.LegalDocuments) && (
        <Route path='/legal-documents'>
          <Route path=':orgId?' element={<LegalDocumentsWrapper />} />
          {privileges.includes(PrivilegeNames.OrganizationSearch_Standard) && (
            <Route path='organization-search' element={<LegalDocumentsOrganizationSearch />} />
          )}
        </Route>
      )}

      {privileges.includes(PrivilegeNames.ObservationReport) && (
        <Route
          path='/observation-report/:eventId/:observedUserId'
          element={<ObservationReportSubmissionPage />}
        />
      )}

      {privileges.includes(PrivilegeNames.ObservationReport_Review) && (
        <Route
          path='/observation-report/:observationReportId'
          element={<ObservationReportReviewPage />}
        />
      )}

      <Route path='/redirect-page/:redirectingData' element={<RedirectPage />} />

      <Route path='/' element={<Navigate to='/data-reports' replace />} />
      <Route path='/page-not-found' element={<NotFoundPage />} />

      <Route path='/permissions' element={<NoPermissionsPage />} />
      <Route path='*' element={<Navigate to='/page-not-found' replace />} />
    </Routes>
  );
};

export default RoutesForRegularRoles;
